import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Heading, HeadingProps } from '@bonusly/design-system';

import { useMonthlyInteraction } from '../hooks/use-interaction';
import { Person } from '../types';
import { PersonStats } from './person-stats';

interface Props {
	people: Person[];
}

export const PersonList: FC<Props> = ({ people }) => {
	const { t } = useTranslation('tracker_widget');

	const { interactionPeopleInMonth, noInteractionPeopleInMonth } = useMonthlyInteraction({ people, type: 'recognition' });

	const peopleToDisplay = useMemo(() => {
		const ordered = noInteractionPeopleInMonth.concat(interactionPeopleInMonth);
		return new Set(ordered);
	}, [interactionPeopleInMonth, noInteractionPeopleInMonth]);

	const [noInteractionToDisplay, interactionToDisplay] = useMemo(() => {
		const unrecognized = noInteractionPeopleInMonth.filter((person) => peopleToDisplay.has(person));
		const recognized = interactionPeopleInMonth.filter((person) => peopleToDisplay.has(person));
		return [unrecognized, recognized];
	}, [peopleToDisplay, noInteractionPeopleInMonth, interactionPeopleInMonth]);

	if (noInteractionToDisplay.length <= 0 && interactionToDisplay.length <= 0) {
		return null;
	}

	return (
		<Flex alignContent="flex-start" flexDirection="column" gap={4}>
			{noInteractionToDisplay.length > 0 && <StyledHeading>{t('person_list.havent_recognized')}</StyledHeading>}
			{noInteractionToDisplay.map((person, index) => {
				return <PersonStats person={person} key={`no-interaction-stat-${index}`} />;
			})}

			{interactionToDisplay.length > 0 && <StyledHeading>{t('person_list.recognized')}</StyledHeading>}

			{interactionToDisplay.map((person, index) => {
				return <PersonStats person={person} key={`interaction-stat-${index}`} />;
			})}
		</Flex>
	);
};

export const StyledHeading = (props: HeadingProps) => {
	return <Heading as="h2" color="text.default" fontSize={100} fontWeight="semibold" lineHeight={6} {...props} />;
};
